import React, { useEffect, useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { ProcedureTemplate, ProcedureType, OwnershipScope, PROCESS_AUDIT_TYPES } from '../../services/auditService';

interface ProcedurePickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    proceduresList: ProcedureTemplate[];
    /** BRD#921: same procedure can appear multiple times — no longer used to block, only for display */
    alreadyLinkedIds: number[];
    /** BRD#924/919/920: the owning scope of the program, used to filter eligible procedures */
    programScope?: OwnershipScope | null;
    /** For Agency-Private programs: the owning agency id to match procedure's owner_agency_id */
    programAgencyId?: number | null;
    onAdd: (procedureId: number) => void;
}

const TYPE_LABELS: Record<ProcedureType, string> = {
    Checklist: 'Checklist',
    Questionnaire: 'Questionnaire',
    Form: 'Form',
    Rating: 'Rating System',
    Confirmation: 'Balance Confirmation',
    PhysicalCheck: 'Physical Inspection',
    Walkthrough: 'Process Walkthrough (legacy)',
    WalkthroughDesign: 'Walkthrough — Test of Design',
    WalkthroughCompliance: 'Walkthrough — Test of Compliance',
    RiskAssessment: 'Risk Assessment',
    ControlTest: 'Control Testing',
};

const ALL_TYPES: ProcedureType[] = [
    'Checklist', 'Questionnaire', 'Form', 'Rating', 'Confirmation',
    'PhysicalCheck', 'WalkthroughDesign', 'WalkthroughCompliance',
    'RiskAssessment', 'ControlTest',
];

const ProcedurePickerModal: React.FC<ProcedurePickerModalProps> = ({
    isOpen, onClose, proceduresList, alreadyLinkedIds,
    programScope, programAgencyId, onAdd,
}) => {
    const [selectedType, setSelectedType] = useState<ProcedureType | ''>('');
    const [selectedId, setSelectedId] = useState<number | ''>('');

    useEffect(() => {
        if (isOpen) {
            setSelectedType('');
            setSelectedId('');
        }
    }, [isOpen]);

    // BRD#925: filter out process-audit types for Agency-Private programs
    const allowedTypes = useMemo((): ProcedureType[] => {
        if (programScope === 'Agency-Private') {
            return ALL_TYPES.filter(t => !(PROCESS_AUDIT_TYPES as string[]).includes(t));
        }
        return ALL_TYPES;
    }, [programScope]);

    const TYPE_OPTIONS = allowedTypes.map(t => ({ value: t, label: TYPE_LABELS[t] }));

    // BRD#922: only active procedures in picker
    // Only approved procedures are linkable — proceduresList carries every
    // workflow state (draft/submitted/rejected leaked into the picker otherwise).
    // BRD#920: GRC-Common programs → only procedures with owner_agency_id = null
    // BRD#919: Agency-Private programs → own agency's procedures + GRC-Common procedures
    const eligibleProcedures = useMemo(() => {
        return proceduresList.filter(p => {
            if ((p.is_active ?? 1) !== 1) return false;
            if (p.workflow_state !== 'approved') return false;
            if (programScope === 'GRC-Common') {
                return p.owner_agency_id == null;
            }
            if (programScope === 'Agency-Private') {
                return p.owner_agency_id == null || p.owner_agency_id === programAgencyId;
            }
            return true;
        });
    }, [proceduresList, programScope, programAgencyId]);

    const proceduresOfType = useMemo(() => {
        if (!selectedType) return [];
        // BRD#921: allow duplicates — do NOT filter out alreadyLinkedIds
        return eligibleProcedures.filter(p => p.procedure_type === selectedType);
    }, [selectedType, eligibleProcedures]);

    const canAdd = selectedType !== '' && selectedId !== '';

    const handleAdd = () => {
        if (!canAdd) return;
        onAdd(Number(selectedId));
    };

    const procedureOptions = proceduresOfType.map(p => ({
        value: p.id,
        label: alreadyLinkedIds.includes(p.id)
            ? `${p.procedure_name} (already linked)`
            : p.procedure_name,
    }));

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="sm">
            <Modal.Header>Link Procedure</Modal.Header>
            <Modal.Body>
                <div className="flex flex-col gap-y-4">
                    <FormControl.SearchSelect
                        label="Procedure Type"
                        required
                        data-testid="SEL_procedure_picker_type"
                        options={TYPE_OPTIONS}
                        value={selectedType || null}
                        onChange={v => {
                            setSelectedType(v ? String(v) as ProcedureType : '');
                            setSelectedId('');
                        }}
                        placeholder="— Select type —"
                    />
                    <FormControl.SearchSelect
                        label="Procedure"
                        required
                        data-testid="SEL_procedure_picker_procedure"
                        options={procedureOptions}
                        value={selectedId || null}
                        onChange={v => setSelectedId(v ? Number(v) : '')}
                        placeholder={selectedType ? '— Select procedure —' : 'Select a type first'}
                        disabled={!selectedType}
                        helper={selectedType && proceduresOfType.length === 0 ? 'No eligible procedures of this type.' : undefined}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="fill" icon={Save} type="button" data-testid="BTN_procedure_picker_submit" onClick={handleAdd} disabled={!canAdd}>
                    Submit
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ProcedurePickerModal;
